import { Api, Fixtuur, ModularSofa } from '@fixtuur/backpack';
import * as Hooks from 'preact/hooks';
import { useSelector } from '../../store/redux';
import * as Selectors from '../../store/selectors';
import { useAsyncThrowError } from '../useAsync';

const formatModularDataForHaymaker = (
    pieces: string[],
    globalSelections: ModularSofa.PropertySelections,
) => {
    const obj: { [key: string]: string | number | null } = {};

    pieces.map((item, idx) => {
        obj[`${idx}`] = item;
    });

    Object.keys(globalSelections).map(key => {
        obj[key] = globalSelections[key];
    });

    return obj;
};

function useSharingModal() {
    const [showSharingModal, setSharingModalOpen] = Hooks.useState(false);
    return {
        showSharingModal,
        openSharingModal: () => setSharingModalOpen(true),
        closeSharingModal: () => setSharingModalOpen(false),
    };
}

export const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

export function useModularSharing(
    modularConfiguratorId: string,
    pieces: string[],
    globalSelections: ModularSofa.PropertySelections,
) {
    const modal = useSharingModal();

    // Selectors and data we need to be able to create and manage sharing
    const currentUrl = useSelector(Selectors.Core.currentUrl);
    const serviceUrls = useSelector(Selectors.Core.serviceConfig);

    const getSharingUrl = useAsyncThrowError(async () => {
        if (!currentUrl || !modal.showSharingModal || !modularConfiguratorId || !serviceUrls) {
            return undefined;
        }

        return Api.Sharing.buildConfigurationLink(
            'explore-config',
            currentUrl,
            {
                configuratorId: modularConfiguratorId,
                selections: {
                    properties: formatModularDataForHaymaker(pieces, globalSelections),
                    attachments: {},
                },
            },
            serviceUrls,
        );
    }, [currentUrl, serviceUrls, modal.showSharingModal]);

    return {
        sharingUrl: () => getSharingUrl?.result,
        ...modal,
    };
}

export function useSharing(inspirationProductId: string | null) {
    const modal = useSharingModal();
    // Display and hide sharing modal and notifications
    const [showAlert, setShowAlert] = Hooks.useState(false);

    // Selectors and data we need to be able to create and manage sharing
    const currentUrl = useSelector(Selectors.Core.currentUrl);
    const serviceUrls = useSelector(Selectors.Core.serviceConfig);
    const productSelections = useSelector(Selectors.Selections.productSelections);
    const attachmentSelections = useSelector(Selectors.Selections.attachmentSelections);
    const buildPropertySelections = useSelector(Selectors.Selections.buildPropertySelections);
    const groupingCodeSelections = useSelector(Selectors.Selections.groupingCodeSelections);
    const configurationSpaceId = useSelector(
        Selectors.Initialisation.currentConfiguratorDefinition,
    )?.configurationSpaceId;
    const isConfiguration = useSelector(Selectors.Initialisation.inConfiguration);

    const loading = !inspirationProductId || !currentUrl;

    const getSharingUrl = useAsyncThrowError(async () => {
        if (!serviceUrls || !currentUrl || !modal.showSharingModal) {
            return undefined;
        }

        const currentSelections =
            productSelections && groupingCodeSelections && buildPropertySelections
                ? Fixtuur.Configuration.Selections.concat(
                      Fixtuur.Configuration.Selections.concat(
                          productSelections,
                          groupingCodeSelections,
                      ),
                      buildPropertySelections,
                  )
                : undefined;

        /**
         *
         * add track event fromShareLink
         *
         */

        if (inspirationProductId) {
            return Api.Sharing.buildInspirationLink(currentUrl, inspirationProductId, serviceUrls);
        } else if (isConfiguration && configurationSpaceId && currentSelections) {
            return Api.Sharing.buildConfigurationLink(
                'explore-config',
                currentUrl,
                {
                    configuratorId: configurationSpaceId,
                    selections: {
                        properties: Fixtuur.Configuration.Selections.toApi(currentSelections),
                        attachments: attachmentSelections ?? {},
                    },
                },
                serviceUrls,
            );
        } else {
            return undefined;
        }
    }, [currentUrl, serviceUrls, modal.showSharingModal, inspirationProductId]);

    return {
        sharingUrl: () => getSharingUrl?.result,
        showAlert,
        setShowAlert,
        modalLoading: loading,
        ...modal,
    };
}
